import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { useMapFullscreen } from "../../context/MapFullscreenContext";
import SabiasQue from "../SabiasQue";
import SinConexionKawaii from "../SinConexionKawaii";

// Template158: Montmartre Mapa + contenido (Muro Te quiero, Moulin Rouge, Cómo llegar). Todo desde backend.
// page.texts[0]=header | page.maps[0]=url | page.sections[0..1] (imageId, title, body, sabiasQue[0]) | page.llegar | page.images (muro, moulin, maison), page.canvasImages (logo)
const Template158 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(false);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(true);
	const { setMapFullscreen } = useMapFullscreen() || {};

	useEffect(() => {
		if (typeof setMapFullscreen === "function") {
			setMapFullscreen(fullscreen);
			return () => setMapFullscreen(false);
		}
	}, [fullscreen, setMapFullscreen]);

	useEffect(() => {
		if (typeof navigator !== "undefined") {
			setIsOnline(navigator.onLine);
			setLoaded(navigator.onLine);
		}
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found) return found.src ?? found.url;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const headerText = page?.texts?.[0]?.content;
	const logoImage = getCanvasImageUrl("logo");
	const mapUrl = page?.maps?.[0];

	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const section0 = sections[0];
	const section1 = sections[1];
	const llegar = page?.llegar ?? page?.data?.llegar ?? page?.data?.data?.llegar;

	const muroImage = getImageSrc(section0?.imageId);
	const moulinImage = getImageSrc(section1?.imageId);
	const maisonImage = getImageSrc("maison");

	const actualHeaderColor = headerBackgroundColor || "#2C3E50";
	const contentColor = textColor || "#444";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	if (fullscreen && mapUrl) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: actualHeaderColor,
					}}
				>
					<Box
						component="button"
						onClick={() => setFullscreen(false)}
						sx={{
							backgroundColor: "#fff",
							color: actualHeaderColor,
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				<iframe
					title="Mapa Montmartre"
					src={mapUrl}
					width="100%"
					height="100%"
					style={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				/>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					{headerText}
					{!isSmallScreen && logoImage && (
						<img
							src={logoImage}
							alt=""
							style={{
								width: 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			<Box
				data-block-id="map"
				sx={{
					flex: 1,
					marginTop: headerText ? "-70px" : 0,
					position: "relative",
					minHeight: isSmallScreen ? 340 : 260,
				}}
			>
				{isOnline && mapUrl && !loaded && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
						}}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: `4px solid ${actualHeaderColor}`,
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
								"@keyframes spin": {
									"0%": { transform: "rotate(0deg)" },
									"100%": { transform: "rotate(360deg)" },
								},
							}}
						/>
					</Box>
				)}

				{isOnline && mapUrl && (
					<Box
						component="button"
						onClick={() => setFullscreen(true)}
						sx={{
							position: "absolute",
							top: 80,
							right: 10,
							backgroundColor: actualHeaderColor,
							color: "#fff!important",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",
							zIndex: 4,
							border: "none",
							cursor: "pointer",
						}}
					>
						Ver mapa grande
					</Box>
				)}

				{isOnline && mapUrl ? (
					<iframe
						title="Mapa Montmartre"
						src={mapUrl}
						width="100%"
						height="100%"
						style={{ border: 0 }}
						onLoad={() => setLoaded(true)}
					/>
				) : !isOnline ? (
					<SinConexionKawaii height="100%" fontFamily={fontFamily} />
				) : !mapUrl ? (
					<Box
						sx={{
							width: "100%",
							height: "100%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							backgroundColor: "#E8F4F8",
						}}
					>
						<Box
							component="p"
							sx={{
								fontFamily: `${
									fontFamily || "'Poppins', sans-serif"
								} !important`,
								color: "#666",
								fontSize: 13,
							}}
						>
							Mapa no configurado
						</Box>
					</Box>
				) : null}

				{maisonImage && (
					<img
						src={maisonImage}
						alt=""
						style={{
							width: 90,
							position: "absolute",
							bottom: -15,
							left: 12,
							zIndex: 3,
						}}
					/>
				)}
			</Box>

			{/* CONTENIDO */}
			<Box
				sx={{
					padding: "10px 12px 20px",
					display: "flex",
					flexDirection: "column",
					gap: "12px",
					marginTop: "12px",
				}}
			>
				{section0 && (
					<Box data-block-id="text-1" sx={{ display: "flex", gap: "16px", position: "relative" }}>
						{muroImage && (
							<img
								src={muroImage}
								alt=""
								style={{
									width: "40%",
									minWidth: "135px",
									height: 100,
									objectFit: "cover",
									borderRadius: "12px",
								}}
							/>
						)}
						<Box sx={{ width: muroImage ? "60%" : "100%" }}>
							{section0.title && (
								<Box component="h3" sx={estiloH3}>
									{section0.title}
								</Box>
							)}
							{section0.body && (
								<Box component="p" sx={estiloP}>
									{section0.body}
								</Box>
							)}
						</Box>
						{section0.sabiasQue?.[0] && (
							<Box sx={{ position: "absolute", top: -8, left: "33%" }}>
								<SabiasQue
									icono={section0.sabiasQue[0].icono}
									texto={section0.sabiasQue[0].texto}
								/>
							</Box>
						)}
					</Box>
				)}

				{section1 && (
					<Box data-block-id="text-2" sx={{ display: "flex", gap: "16px", position: "relative" }}>
						{moulinImage && (
							<img
								src={moulinImage}
								alt=""
								style={{
									width: "40%",
									minWidth: "135px",
									height: 100,
									objectFit: "cover",
									borderRadius: "12px",
								}}
							/>
						)}
						<Box sx={{ width: moulinImage ? "60%" : "100%" }}>
							{section1.title && (
								<Box component="h3" sx={estiloH3}>
									{section1.title}
								</Box>
							)}
							{section1.body && (
								<Box component="p" sx={estiloP}>
									{section1.body}
								</Box>
							)}
						</Box>
						{section1.sabiasQue?.[0] && (
							<Box sx={{ position: "absolute", top: -8, left: "33%" }}>
								<SabiasQue
									icono={section1.sabiasQue[0].icono}
									texto={section1.sabiasQue[0].texto}
								/>
							</Box>
						)}
					</Box>
				)}

				{llegar?.title && (
					<Box
						data-block-id="text-3"
						sx={{
							padding: "12px",
							backgroundColor: "#E8F4F8",
							borderRadius: "12px",
							border: "1px solid #b8d4e0",
						}}
					>
						<Box component="h3" sx={{ ...estiloH3, marginBottom: "8px" }}>
							{llegar.title}
						</Box>
						{llegar.content && (
							<Box
								component="div"
								dangerouslySetInnerHTML={{ __html: llegar.content }}
								sx={estiloP}
							/>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
};

Template158.thumbnail = "/images/template-1.png";

export default Template158;
